/* eslint-disable camelcase */
import { ORDER_SIDE } from 'constants/constants';
import { getImpactFromPremium } from 'formulae/formulae.helpers';
import {
  computeMaxQuantity,
  calculateMaxLossAtSettlementByLegs,
  applyGST,
  convertOrderBook,
} from 'helpers/formulas';
import { division } from 'helpers/math';
import { isEmpty, isOptions } from 'helpers/utils';
import {
  markPriceSelectorByProductId,
  spotPriceSelectorBySpotSymbol,
} from 'stores/ticker/ticker.selectors';
import { OpenPosition } from 'types/IOpenPosition';
import { ContractType } from 'types/IProducts';

export const getNonSpotMaxQuantity = ({
  product,
  balance,
  orderbooks,
  allOpenPositions,
  side,
  limit_price,
  leverage,
  initialLeverage,
}) => {
  if (!product || isEmpty(balance)) return 0;

  const orderbook = convertOrderBook(orderbooks, side);

  const {
    id,
    spot_index,
    contract_value,
    taker_commission_rate,
    contract_type,
    notional_type,
    product_specs,
  } = product;
  const isBuyActionState = side === ORDER_SIDE.BUY;

  const spotPrice = spotPriceSelectorBySpotSymbol(spot_index?.symbol as string);
  const markPrice = markPriceSelectorByProductId(id);

  const availableBalance = Math.max(Number(balance), 0.0);

  const totalPositionSize = allOpenPositions.reduce(
    (acc: number, position: OpenPosition) => {
      if (position?.product?.id === id) {
        return acc + Number(position?.size);
      }
      return acc;
    },
    0
  );

  if (isOptions(contract_type)) {
    // impact price from orderbook assuming there’s no fee and all the AB can be used for the order.
    const { impactPrice: impactByBalance } = getImpactFromPremium(
      Number(availableBalance),
      orderbook,
      product
    );

    // Buy: If ImpactPrice < Limit Price, then Entry Price = Imp else Entry Price = Limit Price
    // Sell: If ImpactPrice > Limit Price, Entry Price = Imp else Entry Price = Limit Price
    const entryPrice = (() => {
      if (Number(limit_price)) {
        if (side === ORDER_SIDE.BUY) {
          return Number(impactByBalance) < Number(limit_price)
            ? Number(impactByBalance)
            : Number(limit_price);
        }
        return Number(impactByBalance) > Number(limit_price)
          ? Number(impactByBalance)
          : Number(limit_price);
      }
      return impactByBalance;
    })() as number;

    let feePerContract =
      Number(spotPrice) * Number(taker_commission_rate) * Number(contract_value);
    if (typeof product.product_specs?.premium_commission_rate === 'number') {
      // Fee = min ({premium_commission_rate} * entry price, spot price * fee_rate) * contract_value
      // fee capping at {premium_commission_rate} of est. premium for options
      const feeByPremium =
        product.product_specs.premium_commission_rate *
        entryPrice *
        Number(contract_value);

      feePerContract = applyGST(Math.min(feePerContract, feeByPremium));
    }

    if (side === ORDER_SIDE.BUY) {
      // MaxNumberofContracts = AB/ (Entry Price * contract_value + 3* Fee)
      const buyMaxQuantity = Math.floor(
        division(
          Number(availableBalance),
          Number(entryPrice) * Number(contract_value) + 3 * Number(feePerContract)
        ) + Math.abs(Math.min(totalPositionSize, 0.0))
      );

      const offSettedMax = Math.min(
        Number(product?.position_size_limit) - Math.max(totalPositionSize, 0.0),
        buyMaxQuantity
      );
      return offSettedMax;
    }

    let marginRequired = 0;
    if (contract_type === ContractType.OptionsCombos) {
      const { legs } = product_specs;
      const max_loss_at_settlement = calculateMaxLossAtSettlementByLegs(legs ?? []);
      marginRequired = Math.min(
        division(Number(spotPrice), leverage),
        Math.abs(max_loss_at_settlement)
      );
    } else {
      marginRequired = division(Number(spotPrice), leverage);
    }
    // todo : MaxLeverage too is dependent on position size. So, ideally, we should use different equations here
    // MaxNumberofContracts = AB/ (Margin_required * contract_value + 3* Fee) + NumberofContracts_Long_Position
    const sellMax = Math.floor(
      division(
        Number(availableBalance),
        Number(marginRequired) * Number(contract_value) + 3 * Number(feePerContract)
      ) + Math.max(totalPositionSize, 0.0)
    );
    const offSettedMax = Math.min(
      Number(product.position_size_limit) - Math.abs(Math.min(totalPositionSize, 0.0)),
      sellMax
    );
    return offSettedMax;
  }

  const maxQuantity = computeMaxQuantity({
    availableBalance,
    entryPrice: Number(limit_price) || Number(markPrice),
    leverage: leverage || initialLeverage,
    isVanillaProduct: notional_type === 'vanilla',
    totalPositionSize,
    isBuyActionState,
    commissionRate: taker_commission_rate,
    contractValue: contract_value,
    product,
    spotPrice,
    markPrice,
  });

  return maxQuantity;
};
